import {
  Button,
  ButtonGroup,
  Center,
  Checkbox,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
  useBoolean,
  useDisclosure,
} from "@chakra-ui/react";
import { Trans, t } from "@lingui/macro";
import {
  useExpenseXeroExportQuery,
  useOutgoingInvoiceXeroExportQuery,
} from "@src/__generated__/urql-graphql";
import { InputDatePicker } from "@src/components/ui-kit";
import { CancelButton } from "@src/components/ui-kit/Button/CancelButton";
import { fieldToCheckboxProps } from "@src/utils/forms/inputHelpers";
import { date, required } from "@src/utils/forms/validators";
import { FieldState, FormState } from "formstate";
import { observer } from "mobx-react-lite";
import { forwardRef, useImperativeHandle, useState } from "react";

export interface XeroExportModalRef {
  open: () => void;
  close: () => void;
  isOpen: boolean;
}

class Form {
  from = new FieldState<Date>(new Date()).validators(required, date);
  to = new FieldState<Date>(new Date()).validators(required, date);
  outgoingInvoices = new FieldState<boolean>(true);
  proformaInvoices = new FieldState<boolean>(true);
  expenses = new FieldState<boolean>(true);

  form = new FormState([
    this.expenses,
    this.outgoingInvoices,
    this.proformaInvoices,
    this.from,
    this.to,
  ]);
}

export const XeroExportModal = observer<object, XeroExportModalRef>(
  forwardRef(function XeroExportModal(_, ref) {
    const [form] = useState(() => new Form());
    const [isSubmitted, setIsSubmitted] = useBoolean(false);
    const { isOpen, onOpen, onClose } = useDisclosure({
      onClose: () => {
        setIsSubmitted.off();
        form.form.reset();
      },
    });

    useImperativeHandle(
      ref,
      () => ({
        open: onOpen,
        close: onClose,
        isOpen,
      }),
      [],
    );

    const queryOptions = {
      variables: {
        from: form.from.$,
        to: form.to.$,
      },
      pause: true,
    };

    const [{ fetching: expensesFetching }, exportExpenses] =
      useExpenseXeroExportQuery(queryOptions);
    const [{ fetching: oiFetching }, exportOutgoingInvoices] =
      useOutgoingInvoiceXeroExportQuery(queryOptions);

    const onExport = async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      const res = await form.from.validate();
      if (res.hasError) return;

      form.expenses.$ && exportExpenses();
      form.outgoingInvoices.$ && exportOutgoingInvoices();

      setIsSubmitted.on();
    };

    return (
      <Modal isOpen={isOpen} onClose={onClose} trapFocus={false}>
        <ModalOverlay />
        <ModalContent>
          <form onSubmit={onExport}>
            <ModalHeader>
              <Trans>Xero export</Trans>
            </ModalHeader>

            <ModalCloseButton />

            <ModalBody>
              {isSubmitted ? (
                <Center>
                  <Text>
                    <Trans>
                      Your export is being prepared. It may take a while. You
                      can close this pop-up and check your data in Xero in 5
                      minutes.
                    </Trans>
                  </Text>
                </Center>
              ) : (
                <Stack>
                  <Checkbox {...fieldToCheckboxProps(form.outgoingInvoices)}>
                    <Trans>Outgoing invoices</Trans>
                  </Checkbox>
                  <Checkbox {...fieldToCheckboxProps(form.expenses)}>
                    <Trans>Expenses</Trans>
                  </Checkbox>

                  <Stack pt="3" spacing="3">
                    <InputDatePicker
                      label={t`From`}
                      selected={form.from.value}
                      onChange={(val) => {
                        if (val) {
                          form.from.onChange(val.start);
                        }
                      }}
                      error={form.from.error}
                    />

                    <InputDatePicker
                      label={t`To`}
                      selected={form.to.value}
                      onChange={(val) => {
                        if (val) {
                          form.to.onChange(val.start);
                        }
                      }}
                      error={form.to.error}
                    />
                  </Stack>
                </Stack>
              )}
            </ModalBody>

            <ModalFooter>
              <ButtonGroup>
                <CancelButton onClick={onClose} />
                {!isSubmitted && (
                  <Button
                    isDisabled={
                      !form.expenses.value &&
                      !form.outgoingInvoices.value &&
                      !form.proformaInvoices.value
                    }
                    isLoading={expensesFetching || oiFetching}
                    type="submit"
                  >
                    <Trans>Export</Trans>
                  </Button>
                )}
              </ButtonGroup>
            </ModalFooter>
          </form>
        </ModalContent>
      </Modal>
    );
  }),
);
